// src/app/shared/services/legal-systems.service.ts

import {
  Footer,
  LegalSystemKey,
  LegalSystemPage,
  LegalSystemsDataMap,
  Menu,
  SharedHeader
} from '../shared/legal-system.model'
import { Observable, catchError, map, shareReplay, throwError } from 'rxjs'

import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'

@Injectable({ providedIn: 'root' })
export class LegalSystemsService {
  private readonly url = 'assets/data/legal-systems.json'

  // Load once, cache forever (until refresh)
  private readonly data$: Observable<LegalSystemsDataMap> = this.http
    .get<LegalSystemsDataMap>(this.url)
    .pipe(
      shareReplay(1),
      catchError(err =>
        throwError(
          () => new Error(`Failed to load ${this.url}. ${err?.message ?? err}`)
        )
      )
    )

  constructor (private http: HttpClient) {}

  /** Get the whole map (rarely needed) */
  getAll (): Observable<LegalSystemsDataMap> {
    return this.data$
  }

  /** Get a single page by key (what you asked for) */
  getByKey (key: LegalSystemKey): Observable<LegalSystemPage> {
    return this.data$.pipe(
      map(data => {
        const page = data[key]
        if (!page) {
          throw new Error(`Key "${key}" not found in legal-systems.json`)
        }
        // Optional safety: ensure JSON key matches object key
        if (page.key !== key) {
          throw new Error(
            `Key mismatch: object.key="${page.key}" but requested "${key}"`
          )
        }
        return page
      })
    )
  }

  getSharedHeader (key: string): Observable<SharedHeader> {
    return this.data$.pipe(
      map(data => data[key as keyof LegalSystemsDataMap] as SharedHeader)
    )
  }

  getSharedFooter (key: string): Observable<Footer> {
    return this.data$.pipe(
      map(data => data[key as keyof LegalSystemsDataMap] as Footer)
    )
  }

  getSharedMenu (key: string): Observable<Menu> {
    return this.data$.pipe(
      map(data => data[key as keyof LegalSystemsDataMap] as Menu)
    )
  }
}
